/**
 * Chrome Component
 * Smart component that shows/hides children based on layout configuration
 */

import React, { type ReactNode, type ComponentType, type FC } from 'react';
import { useLayout } from '@/contexts/LayoutContext';
import type { ChromeComponent } from '@/lib/layout/types';

interface ChromeProps {
  component: ChromeComponent;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Wrapper component that conditionally renders children based on layout config
 */
export const Chrome: React.FC<ChromeProps> = ({ component, children, fallback = null }) => {
  const { shouldShow } = useLayout();
  return shouldShow(component) ? <>{children}</> : <>{fallback}</>;
};

/**
 * Higher-Order Component that wraps a component with chrome visibility logic
 */
export function withChrome<P extends object>(
  Component: ComponentType<P>,
  component: ChromeComponent
): FC<P> {
  return (props: P) => {
    const { shouldShow } = useLayout();
    return shouldShow(component) ? <Component {...props} /> : null;
  };
}

/**
 * Hook that returns whether a chrome component should be visible
 */
export function useChromeVisibility(component: ChromeComponent): boolean {
  const { shouldShow } = useLayout();
  return shouldShow(component);
}
